import cn from "classnames";
import { Suspense, useRef, useState } from "react";

import DropdownArrowDownV2 from "assets/icons/DropdownArrowDownV2.svg?react";
import DropdownArrowUpV2 from "assets/icons/DropdownArrowUpV2.svg?react";

import LoadingOverlap from "components/LoadingOverlap";

import styles from "./Directory.module.scss";

interface DirectoryProps {
  title: string;
  icon: React.ReactNode;
  controls: React.ReactNode;
  body: React.ReactNode;
  isCollapsible: boolean;
  isOpen: boolean;
  isResizable: boolean;
  flexGrow: number;
  onToggle: () => void;
  onResizeStart: () => void;
  onResizeMove: (deltaY: number) => void;
  onResizeEnd: () => void;
}

export const Directory = (props: DirectoryProps) => {
  const {
    title,
    icon,
    controls,
    body,
    isCollapsible,
    isOpen,
    isResizable,
    flexGrow,
    onToggle,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  } = props;

  const resizeRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startYRef = useRef<number>(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isResizable || !resizeRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    startYRef.current = e.clientY;

    onResizeStart();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startYRef.current;
      onResizeMove(deltaY);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      onResizeEnd();
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      className={cn(styles.directory, {
        [styles.open]: isOpen,
        [styles.collapsible]: isCollapsible,
      })}
      ref={resizeRef}
      style={{ flexGrow }}
      data-resizing={isResizing}
      data-open={isOpen}
    >
      {isResizable && (
        <div
          className={styles.resizeHandle}
          onMouseDown={handleMouseDown}
          data-resize-handle
        />
      )}
      <div className={styles.header}>
        <div
          className={styles.left}
          onClick={() => {
            if (isCollapsible && !isOpen) {
              onToggle();
            }
          }}
        >
          <div className={styles.icon}>{icon}</div>
          <div className={styles.title}>{title}</div>
        </div>
        <div className={styles.right}>
          <div className={styles.controls}>{controls}</div>
          {isCollapsible && (
            <div
              className={styles.toggle}
              onClick={onToggle}
            >
              {isOpen ? <DropdownArrowDownV2 /> : <DropdownArrowUpV2 />}
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <div className={styles.body}>
          <Suspense
            fallback={
              <div style={{ height: "100%" }}>
                <LoadingOverlap isLoading />
              </div>
            }
          >
            {body}
          </Suspense>
        </div>
      )}
    </div>
  );
};
